"use client";
import React, { useEffect, useRef, useState } from "react";
import styles from "./WhyBusinessesChoose.module.css";

const CARDS = [
    {
        icon: "fa-solid fa-award",
        eyebrow: "Proven Expertise",
        title: "10+ Years of E‑Commerce Engineering",
        body:
            "We’ve built end‑to‑end multi‑vendor platforms across fashion, electronics, automotive, food delivery, and B2B trade — from architecture to automation and analytics.",
    },
    {
        icon: "fa-solid fa-cloud-arrow-up",
        eyebrow: "Scalable Cloud Infrastructure",
        title: "Deploy Once, Scale Infinitely",
        body:
            "Modular microservices and cloud‑native builds with auto‑scaling, edge caching, and CI/CD pipelines ensure zero‑downtime growth.",
    },
    {
        icon: "fa-solid fa-brain",
        eyebrow: "AI‑Driven Personalization",
        title: "Smarter Buying Experiences",
        body:
            "Machine learning boosts engagement with intelligent recommendations, behavior prediction, cross‑sell logic, and dynamic pricing.",
    },
    {
        icon: "fa-solid fa-shield-halved",
        eyebrow: "Enterprise Security",
        title: "Multi‑Layer Protection for Every Transaction",
        body:
            "ISO‑grade encryption, 2FA vendor access, secure payment routing, GDPR readiness, OWASP compliance, and audit logs built‑in.",
    },
];

const METRICS = [
  { icon: "fa-solid fa-globe", label: "Live Marketplaces", value: 30, suffix: "+" },
  { icon: "fa-solid fa-bolt", label: "Transactions Processed", value: 10_000_000, compact: true, suffix: "+" },
  { icon: "fa-solid fa-briefcase", label: "Active Vendors", value: 150, suffix: "+" },
  { icon: "fa-solid fa-rocket", label: "Server Uptime", value: 99.9, decimals: 1, suffix: "%" },
];

export default function WhyBusinessesChoose() {
  const sectionRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [counts, setCounts] = useState(METRICS.map(() => 0));
  const particles = [
    { top: "12%", delay: "0s" },
    { top: "28%", delay: "2s" },
    { top: "46%", delay: "3s" },
    { top: "62%", delay: "5s" },
    { top: "74%", delay: "6s" },
  ];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setStarted(true)),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let raf;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const start = performance.now();
    const durations = METRICS.map((m, i) => 1200 + i * 300);
    const tick = (now) => {
      const arr = METRICS.map((m, i) => {
        if (prefersReduced) return m.value;
        const p = Math.min(1, (now - start) / durations[i]);
        const eased = p * (2 - p);
        return m.value * eased;
      });
      setCounts(arr);
      if (!prefersReduced && arr.some((v, i) => v < METRICS[i].value)) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started]);

  return (
    <section ref={sectionRef} className={`${styles.section} py-5 py-lg-6`}>
      {/* Row 1: Header */}
      <div className={styles.hero}>
        <div className={styles.heroBg} aria-hidden>
          {particles.map((p, i) => (
            <i key={i} style={{ top: p.top, left: `${i * 18}%`, animationDelay: p.delay }} />
          ))}
        </div>
        <header className={styles.head}>
          <h3 className={styles.title}>Chosen by Visionary Brands — Trusted for Scalable, Intelligent Commerce.</h3>
          <p className={styles.subtitle}>
            From emerging startups to enterprise networks, CodeQlik empowers businesses with future‑ready multi‑vendor systems that scale effortlessly, perform flawlessly, and deliver measurable growth.
          </p>
        </header>
      </div>

      {/* Row 2: Radial / Circular layout */}
      <RadialShowcase counts={counts} />
  </section>
  );
}

function formatValue(val, i) {
  const m = METRICS[i];
  if (m?.compact) {
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}k`;
    return Math.round(val).toString();
  }
  if (m?.decimals != null) return Number(val).toFixed(m.decimals);
  return Math.round(val).toString();
}

function RadialShowcase({ counts }) {
  const radialRef = useRef(null);
  const hubRef = useRef(null);
  const blockRefs = useRef([]);
  const prevPosRef = useRef([]);
  const clearedRef = useRef(false);
  const [positions, setPositions] = useState([]);
  const [lines, setLines] = useState([]);
  const [hover, setHover] = useState(-1);
  const [modal, setModal] = useState(null);
  const [stacked, setStacked] = useState(false);
  // Classic diagonals (45°/135°/225°/315°) maximize horizontal separation of the top pair
  // Order: TR(45°), TL(135°), BL(225°), BR(315°)
  const ANG = React.useMemo(() => [45, 135, 225, 315], []);
  const cardsReady = (blockRefs.current.filter(Boolean).length === CARDS.length);

  useEffect(() => {
    const root = radialRef.current;
    if (!root) return;
    const recompute = () => {
      const r = root.getBoundingClientRect();
      const isStacked = r.width < 768;
      setStacked(isStacked);
      if (isStacked) {
        // stacked layout: hide connectors/orbits, let CSS place nodes
        if (!clearedRef.current) {
          prevPosRef.current = [];
          setPositions([]);
          setLines([]);
          clearedRef.current = true;
        }
        return;
      }
      clearedRef.current = false;
      const cx = r.width / 2;
      const cy = r.height / 2;
      // derive ellipse radii (Rx, Ry) and per-side angles from actual card sizes to avoid any overlap
      const rects = blockRefs.current.filter(Boolean).map(el => el.getBoundingClientRect());
      const cW = (rects.length ? Math.max(...rects.map(r => r.width)) : 280);
      const cH = (rects.length ? Math.max(...rects.map(r => r.height)) : 420);
      const margin = 24;    // edge padding
      const gap = 130;      // desired visible gap between cards (increased)
      // Limits for ellipse radii based on container
      const hw = rects.map(r => (r?.width || cW) / 2);
      const hh = rects.map(r => (r?.height || cH) / 2);
      const maxRx = cx - Math.max(...hw) - margin;
      const maxRy = cy - Math.max(...hh) - margin;
      let Ry = Math.max(260, Math.min(maxRy, cy - 80));

      // compute needed angles from vertical separation with current Ry
      const reqRight = (hh[0] + hh[3] + gap);
      const reqLeft  = (hh[1] + hh[2] + gap);
      const tR = Math.asin(Math.min(0.95, Math.max(0.1, reqRight / (2 * Ry)))) || (Math.PI/4);
      const tL = Math.asin(Math.min(0.95, Math.max(0.1, reqLeft  / (2 * Ry)))) || (Math.PI/4);
      // compute Rx required for top/bottom pairs horizontally
      const rxNeededTop    = (hw[0] + hw[1] + gap) / (Math.cos(tR) + Math.cos(tL));
      const rxNeededBottom = (hw[2] + hw[3] + gap) / (Math.cos(tR) + Math.cos(tL));
      let Rx = Math.max(260, rxNeededTop, rxNeededBottom);
      // if Rx exceeds max, try to increase Ry to reduce angles (increase cos)
      let guard = 0;
      while (Rx > maxRx && Ry + 20 <= maxRy && guard < 40) {
        Ry += 20;
        const tR2 = Math.asin(Math.min(0.95, Math.max(0.1, reqRight / (2 * Ry))));
        const tL2 = Math.asin(Math.min(0.95, Math.max(0.1, reqLeft  / (2 * Ry))));
        const newRx = Math.max(260,
          (hw[0] + hw[1] + gap) / (Math.cos(tR2) + Math.cos(tL2)),
          (hw[2] + hw[3] + gap) / (Math.cos(tR2) + Math.cos(tL2))
        );
        Rx = newRx;
        guard++;
      }
      Rx = Math.min(Rx, maxRx);

      // Final angles using final Ry
      const tRfinal = Math.asin(Math.min(0.95, Math.max(0.1, reqRight / (2 * Ry)))) || (Math.PI/4);
      const tLfinal = Math.asin(Math.min(0.95, Math.max(0.1, reqLeft  / (2 * Ry)))) || (Math.PI/4);
      const ang = [tRfinal, Math.PI - tLfinal, Math.PI + tLfinal, -tRfinal];
      let nextPos = ang.map(rad => ({ x: cx + Rx * Math.cos(rad), y: cy + Ry * Math.sin(rad) }));
      // Safety: if still out-of-bounds or horizontally overlapping, fallback to quadrant corners
      const withinBounds = nextPos.every((p, i) => (
        p.x - hw[i] - margin >= 0 && p.x + hw[i] + margin <= r.width &&
        p.y - hh[i] - margin >= 0 && p.y + hh[i] + margin <= r.height
      ));
      const sepTop = (nextPos[0].x - hw[0]) - (nextPos[1].x + hw[1]) - gap;      // TR vs TL
      const sepBottom = (nextPos[3].x - hw[3]) - (nextPos[2].x + hw[2]) - gap;   // BR vs BL
      if (!(withinBounds && sepTop > 0 && sepBottom > 0)) {
        const pad = Math.max(48, gap / 2);
        const tr = { x: r.width - (pad + hw[0]), y: pad + hh[0] };
        const tl = { x: pad + hw[1],             y: pad + hh[1] };
        const bl = { x: pad + hw[2],             y: r.height - (pad + hh[2]) };
        const br = { x: r.width - (pad + hw[3]), y: r.height - (pad + hh[3]) };
        nextPos = [tr, tl, bl, br];
      }
      // update only if changed to avoid loops (compare with ref)
      const prev = prevPosRef.current || [];
      const same = prev.length === nextPos.length && prev.every((p, i) => Math.abs(p.x - nextPos[i].x) < 0.5 && Math.abs(p.y - nextPos[i].y) < 0.5);
      if (!same) {
        setPositions(nextPos);
        const hub = { x: cx, y: cy };
        const out = nextPos.map((p) => ({ from: { x: p.x, y: p.y }, to: { x: hub.x, y: hub.y }, curve: (hub.y - p.y) * 0.18 }));
        setLines(out);
        prevPosRef.current = nextPos;
      }
    };
    // run now, next frame, and after a short delay to ensure DOM sizes are ready
    recompute();
    const rafId = requestAnimationFrame(recompute);
    const tId = setTimeout(recompute, 120);
    window.addEventListener("resize", recompute);
    return () => { window.removeEventListener("resize", recompute); cancelAnimationFrame(rafId); clearTimeout(tId); };
  }, [ANG, cardsReady]);

  return (
    <div ref={radialRef} className={`${styles.radial} ${stacked ? styles.stacked : ''}`}>
      {/* SVG connectors */}
      {!stacked && (
      <svg className={styles.radialSvg} width="100%" height="100%" viewBox={`0 0 ${radialRef.current?.clientWidth || 960} ${radialRef.current?.clientHeight || 720}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="radial-line" x1="0" x2="1">
            <stop offset="0%" stopColor="rgba(245,131,39,0)" />
            <stop offset="50%" stopColor="rgba(245,131,39,0.9)" />
            <stop offset="100%" stopColor="rgba(245,131,39,0)" />
          </linearGradient>
          <filter id="radial-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {lines.map((ln, i) => {
          const mx = (ln.from.x + ln.to.x) / 2;
          const my = (ln.from.y + ln.to.y) / 2 + ln.curve;
          const d = `M ${ln.from.x},${ln.from.y} Q ${mx},${my} ${ln.to.x},${ln.to.y}`;
          return <path key={i} d={d} stroke="url(#radial-line)" strokeWidth={2.5} fill="none" filter="url(#radial-glow)" strokeLinecap="round" />;
        })}
        {/* subtle hub ring */}
        <circle cx="50%" cy="50%" r="84" stroke="rgba(245,131,39,0.25)" strokeWidth="1.5" fill="none" />
      </svg>
      )}

      {/* Central hub */}
      <div ref={hubRef} className={styles.hub}>
        <div className={styles.hubInner}>
          <i className="fa-solid fa-server"></i>
          <span>CodeQlik Platform</span>
        </div>
      </div>

      {/* Orbiting metrics */}
      {!stacked && (
      <div className={styles.orbit} aria-hidden>
        <div className={styles.track}>
          {METRICS.map((m, i) => (
            <div key={m.label} className={`${styles.chip} ${styles[`chip${i}`]}`}>
              <i className={m.icon}></i>
              <span>{formatValue(counts[i], i)}{m.suffix || ''}</span>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Four radial cards */}
      {CARDS.map((c, i) => {
        const p = positions[i] || { x: 0, y: 0 };
        const style = stacked ? undefined : { left: p.x, top: p.y, transform: `translate(-50%, -50%) scale(${hover === i ? 1.06 : 1})`, visibility: positions.length === CARDS.length ? 'visible' : 'hidden' };
        return (
          <article
            key={c.eyebrow}
            ref={(el) => (blockRefs.current[i] = el)}
            className={`${styles.node} ${hover === i ? styles.active : ''}`}
            style={style}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(-1)}
            onClick={() => setModal({ index: i })}
          >
            <div className={styles.iconBadge}><i className={c.icon}></i></div>
            <p className={styles.eyebrow}>{c.eyebrow}</p>
            <h5 className={styles.cardTitle}>{c.title}</h5>
            <p className={styles.cardBody}>{c.body}</p>
          </article>
        );
      })}

      {modal && (
        <div className={styles.modalBackdrop} onClick={() => setModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setModal(null)} aria-label="Close">×</button>
            <div className={styles.modalHeader}>
              <i className={CARDS[modal.index].icon}></i>
              <h5>{CARDS[modal.index].title}</h5>
            </div>
            <div className={styles.modalBody}>
              <p>{CARDS[modal.index].body}</p>
              <div className={styles.modalStats}>
                {METRICS.map((m, i) => (
                  <div key={m.label} className={styles.modalStat}><i className={m.icon}></i> {formatValue(counts[i], i)}{m.suffix || ''} <span>{m.label}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}
